import { motion } from "framer-motion";
import { AGENTS } from "@/constants/testIds";
import {
  Activity,
  CalendarRange,
  Eye,
  Fingerprint,
  Headset,
  Wrench,
  Sparkles,
} from "lucide-react";

const AGENT_LIST = [
  {
    id: "diagnostic",
    name: "Diagnostic Agent",
    tag: "AGT-01",
    Icon: Activity,
    span: "md:col-span-2 md:row-span-2",
    body: "Interprets DTC codes, live sensor streams, and freeze-frame data. Ranks probable causes with confidence intervals. Never a single 'check engine' guess.",
    accent: "mclaren",
  },
  {
    id: "maintenance",
    name: "Maintenance Planner",
    tag: "AGT-02",
    Icon: CalendarRange,
    span: "md:col-span-2",
    body: "Builds a 24-month timeline tuned to your driving pattern, climate, and OEM intervals.",
    accent: "cyan",
  },
  {
    id: "repair",
    name: "Repair Advisor",
    tag: "AGT-03",
    Icon: Wrench,
    span: "md:col-span-1",
    body: "Torque specs, tool lists, OEM parts. Mechanic-grade.",
    accent: "cyan",
  },
  {
    id: "vision",
    name: "Vision Inspector",
    tag: "AGT-04",
    Icon: Eye,
    span: "md:col-span-1",
    body: "Detects fluid leaks, cracks, corrosion from any photo.",
    accent: "mclaren",
  },
  {
    id: "vin",
    name: "VIN Insights",
    tag: "AGT-05",
    Icon: Fingerprint,
    span: "md:col-span-2",
    body: "Unlocks factory options, TSBs, recalls, and full service history the moment we identify your car.",
    accent: "cyan",
  },
  {
    id: "concierge",
    name: "Service Concierge",
    tag: "AGT-06",
    Icon: Headset,
    span: "md:col-span-2",
    body: "Routes to vetted shops, negotiates quotes, and schedules pickup. End-to-end.",
    accent: "mclaren",
  },
];

const AgentCard = ({ a, i }) => {
  const accentClass = "text-mymech-blue";
  const borderHover =
    a.accent === "cyan" ? "hover:border-cyan-electric/40" : "hover:border-mclaren/40";
  return (
    <motion.article
      data-testid={AGENTS.card(a.id)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: i * 0.06 }}
      className={`group relative bg-obsidian border border-white/10 ${borderHover} transition-all duration-500 p-8 lg:p-10 overflow-hidden ${a.span}`}
    >
      {/* Corner tag */}
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-center gap-2">
          <span className={`h-1.5 w-1.5 ${a.accent === "cyan" ? "bg-cyan-electric" : "bg-mclaren"} animate-pulse-dot`} />
          <span className="font-mono-tech text-[11px] uppercase tracking-widest text-white/40">
            {a.tag}
          </span>
        </div>
        <a.Icon
          className={`h-6 w-6 ${accentClass} transition-transform group-hover:scale-110`}
          strokeWidth={1.4}
        />
      </div>

      <h3 className="font-display text-2xl lg:text-3xl font-semibold text-white tracking-tight mb-3">
        {a.name}
      </h3>
      <p className="text-white/55 text-sm lg:text-[15px] leading-relaxed font-light max-w-md">
        {a.body}
      </p>

      {/* Ghost grid background */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Hover glow */}
      <div
        className={`pointer-events-none absolute -bottom-24 -right-24 h-64 w-64 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 ${
          a.accent === "cyan" ? "bg-cyan-electric/20" : "bg-mclaren/20"
        }`}
      />
    </motion.article>
  );
};

export const AIAgentsBento = () => {
  return (
    <section
      id="agents"
      data-testid={AGENTS.section}
      className="relative bg-void py-24 lg:py-40 border-t border-white/5"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="max-w-3xl mb-16 lg:mb-24">
          <span className="chip-brand">/ 03 · the crew</span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tighter leading-[0.95]">
            Six agents.<br />
            <span className="text-white/40">One garage.</span>
          </h2>
          <p className="mt-6 text-white/60 text-base sm:text-lg font-light leading-relaxed">
            Each agent is trained on a narrow, deep slice of automotive knowledge.
            then they collaborate on your car like a shop with no ego and no upsell.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 lg:gap-5">
          {AGENT_LIST.map((a, i) => (
            <AgentCard key={a.id} a={a} i={i} />
          ))}
        </div>

        <div className="mt-16 flex items-center gap-3 text-white/40 font-mono-tech text-[11px] uppercase tracking-widest">
          <Sparkles className="h-3.5 w-3.5 text-mclaren" />
          More agents in training. Fleet Ops, Emissions, EV Battery Health.
        </div>
      </div>
    </section>
  );
};

export default AIAgentsBento;
